import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import {
    AGGREGATOR_CREATE_FORM_SUBMITTED,
    AGGREGATOR_FOODMEOW_STORES_ERROR,
    AGGREGATOR_FOODMEOW_STORES_REQUESTED,
    AGGREGATOR_FOODMEOW_STORES_SUCCESS,
    AGGREGATOR_INTEGRATE_FORM_SUBMITTED,
    AGGREGATOR_INTEGRATION_REQUESTED,
    AGGREGATOR_UBER_STORES_ERROR,
    AGGREGATOR_UBER_STORES_REQUESTED,
    AGGREGATOR_UBER_STORES_SUCCESS,
    DELETE_TERMINAL_CLICKED,
    FETCH_LOCATION_DETAIL_SUCCESS,
    FETCH_LOCATIONS_LIST_SUCCESS,
    FETCH_LOCATIONS_SUCCESS,
    FETCH_SERVICE_DETAIL_ERROR,
    FETCH_SERVICE_DETAIL_SUCCESS,
    INTEGRATE_AGGREGATOR_SUCCESS,
    LOCATION_DETAIL_AGENDA_MOUNTED,
    LOCATION_FORM_UNMOUNTED,
    AGGREGATOR_SERVICE_DETAIL_FORM_AGENDA_MOUNTED,
} from "./actions"
import {
    GOOGLE_BUSINESS_INFO_FETCH_REQUESTED,
    GOOGLE_BUSINESS_INFO_FETCH_SUCCEEDED,
    GOOGLE_BUSINESS_INFO_FETCH_FAILED,
    GOOGLE_BUSINESS_LOCATION_SELECTED,
    GOOGLE_BUSINESS_LOCATION_PICKER_CLOSED,
    GOOGLE_BUSINESS_INTEGRATION_FAILED,
    GOOGLE_BUSINESS_INTEGRATION_SUCCEEDED,
    GOOGLE_BUSINESS_INTEGRATION_REMOVE_REQUESTED,
    GOOGLE_BUSINESS_INTEGRATION_REMOVE_SUCCEEDED,
    GOOGLE_BUSINESS_INTEGRATION_REMOVE_FAILED,
} from "./googleBusiness/actions"
import {
    LOCATION_CREATED,
    LOCATION_DELETED,
    LOCATION_UPDATED,
    TERMINAL_CREATED,
    TERMINAL_UPDATED,
} from "../../data/actions"
import type {
    FoodmeowStoreResponse,
    Location,
    LocationDetail,
    LocationsListItem,
    ServiceDetail,
    UberStoreResponse,
} from "../../../graphql/types"
import type { GoogleBusinessInfoResponse } from "./googleBusiness/types"
import { TerminalStatus } from "../../../graphql/types"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../auth/actions"
import { reject } from "ramda"

export type TerminalState = {
    data: Maybe<Location[]>
    // deprecated
    locationsList: Maybe<LocationsListItem[]>
    locationDetail: Maybe<LocationDetail>
    loadingServiceDetail: boolean
    serviceDetail: Maybe<ServiceDetail>
    aggregatorForm: {
        loading: boolean
        error: Maybe<Error>
        uber: Maybe<UberStoreResponse>
        foodmeow: Maybe<FoodmeowStoreResponse>
    }
    googleBusinessInfo: Maybe<GoogleBusinessInfoResponse>
    googleBusinessInfoError: Maybe<string>
    googleBusinessInfoRequested: boolean
    googleBusinessIntegrationError: Maybe<string>
    isSubmittingGoogleBusinessIntegration: boolean
    removeGoogleIntegrationError: Maybe<string>
    removeGoogleIntegrationRequested: boolean
}

export const createDefaultTerminalState = () => ({
    data: null,
    locationsList: null,
    locationDetail: null,
    loadingServiceDetail: false,
    serviceDetail: null,
    uploadedImages: [],
    imageIdsToDelete: [],
    selectedKioskTerminalIds: [],
    aggregatorForm: {
        loading: false,
        error: null,
        uber: null,
        foodmeow: null,
    },
    googleBusinessInfo: null,
    googleBusinessInfoError: null,
    googleBusinessInfoRequested: false,
    googleBusinessIntegrationError: null,
    isSubmittingGoogleBusinessIntegration: false,
    removeGoogleIntegrationError: null,
    removeGoogleIntegrationRequested: false,
})
export const terminalReducer = (
    state: Maybe<TerminalState> = undefined,
    action: Record<string, any>
): TerminalState => {
    if (!state) {
        return createDefaultTerminalState()
    }

    switch (action.type) {
        case ACCOUNT_SELECTED:
        case REFETCH_DATA:
            return createDefaultTerminalState()

        case FETCH_LOCATIONS_SUCCESS:
            return { ...state, data: action.payload.locations }

        case FETCH_LOCATIONS_LIST_SUCCESS:
            return { ...state, locationsList: action.payload.data }

        case FETCH_LOCATION_DETAIL_SUCCESS:
            return { ...state, locationDetail: action.payload.data }

        case FETCH_SERVICE_DETAIL_SUCCESS:
            return { ...state, loadingServiceDetail: false, serviceDetail: action.payload.data }

        case FETCH_SERVICE_DETAIL_ERROR:
            return { ...state, loadingServiceDetail: false }

        case GOOGLE_BUSINESS_INFO_FETCH_REQUESTED:
            return {
                ...state,
                googleBusinessInfoRequested: true,
                googleBusinessInfo: null,
                googleBusinessInfoError: null,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_INFO_FETCH_SUCCEEDED:
            return {
                ...state,
                googleBusinessInfoRequested: false,
                googleBusinessInfo: action.payload,
                googleBusinessInfoError: null,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_INFO_FETCH_FAILED:
            return {
                ...state,
                googleBusinessInfoRequested: false,
                googleBusinessInfo: null,
                googleBusinessInfoError: action.payload.error,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_LOCATION_SELECTED:
            return {
                ...state,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: true,
            }

        case GOOGLE_BUSINESS_LOCATION_PICKER_CLOSED:
            return {
                ...state,
                googleBusinessInfoRequested: false,
                googleBusinessInfo: null,
                googleBusinessInfoError: null,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_INTEGRATION_SUCCEEDED:
            return {
                ...state,
                googleBusinessInfoRequested: false,
                googleBusinessInfo: null,
                googleBusinessInfoError: null,
                googleBusinessIntegrationError: null,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_INTEGRATION_FAILED:
            return {
                ...state,
                googleBusinessIntegrationError: action.payload.error,
                isSubmittingGoogleBusinessIntegration: false,
            }

        case GOOGLE_BUSINESS_INTEGRATION_REMOVE_REQUESTED:
            return {
                ...state,
                removeGoogleIntegrationRequested: true,
                removeGoogleIntegrationError: null,
            }

        case GOOGLE_BUSINESS_INTEGRATION_REMOVE_SUCCEEDED:
            return {
                ...state,
                removeGoogleIntegrationError: null,
                removeGoogleIntegrationRequested: false,
            }

        case GOOGLE_BUSINESS_INTEGRATION_REMOVE_FAILED:
            return {
                ...state,
                removeGoogleIntegrationError: action.payload.error,
                removeGoogleIntegrationRequested: false,
            }

        // Locations
        case LOCATION_CREATED: {
            return {
                ...state,
                data: [...(state.data || []), { ...action.payload.location, terminals: [] }],
                locationsList: [
                    ...(state.locationsList || []),
                    {
                        id: action.payload.location.id,
                        name: action.payload.location.name,
                        timezone: action.payload.location.timezone,
                        children: [],
                    },
                ],
            }
        }

        case LOCATION_UPDATED: {
            return {
                ...state,
                data: (state.data || []).map(l =>
                    l.id !== action.payload.newValue.id
                        ? l
                        : { ...l, ...action.payload.newValue, terminals: l.terminals }
                ),
                locationsList: (state.locationsList || []).map(l =>
                    l.id !== action.payload.newValue.id
                        ? l
                        : { ...l, name: action.payload.newValue.name, timezone: action.payload.newValue.timezone }
                ),
            }
        }

        case LOCATION_DELETED: {
            return {
                ...state,
                data: (state.data || []).filter(l => l.id !== action.payload.locationId),
                locationsList: (state.locationsList || []).filter(l => l.id !== action.payload.locationId),
            }
        }

        case TERMINAL_UPDATED: {
            return {
                ...state,
                data: (state.data || []).map(location =>
                    location.id !== action.payload.newValue.locationId
                        ? location
                        : {
                              ...location,
                              terminals: location.terminals.map(t =>
                                  t.id !== action.payload.newValue.id
                                      ? t
                                      : {
                                            ...t,
                                            ...action.payload.newValue,
                                            instructions: action.payload.newValue.kioskCustomerInstructions,
                                            status: action.payload.newValue.isActive
                                                ? TerminalStatus.Active
                                                : TerminalStatus.Inactive,
                                        }
                              ),
                          }
                ),
                locationsList: (state.locationsList || []).map(l =>
                    l.id !== action.payload.newValue.locationId
                        ? l
                        : {
                              ...l,
                              children: l.children.map(s =>
                                  s.id !== action.payload.newValue.id
                                      ? s
                                      : {
                                            ...s,
                                            ...reject(v => v === undefined, {
                                                locationId: action.payload.newValue.locationId,
                                                isActive: action.payload.newValue.isActive,
                                                name: action.payload.newValue.name,
                                                type: action.payload.newValue.type,
                                                url: action.payload.newValue.url,
                                                metadata: action.payload.newValue.metadata,
                                            }),
                                        }
                              ),
                          }
                ),
            }
        }

        case DELETE_TERMINAL_CLICKED: {
            return {
                ...state,
                data: (state.data || []).map(location =>
                    location.id !== action.payload.locationId
                        ? location
                        : { ...location, terminals: location.terminals.filter(t => t.id !== action.payload.terminalId) }
                ),
                locationsList: (state.locationsList || []).map(location =>
                    location.id !== action.payload.locationId
                        ? location
                        : { ...location, children: location.children.filter(t => t.id !== action.payload.terminalId) }
                ),
            }
        }

        case TERMINAL_CREATED: {
            return {
                ...state,
                data: (state.data || []).map(location =>
                    location.id !== action.payload.terminal.locationId
                        ? location
                        : { ...location, terminals: [...location.terminals, action.payload.terminal] }
                ),
                locationsList: (state.locationsList || []).map(l =>
                    l.id !== action.payload.terminal.locationId
                        ? l
                        : {
                              ...l,
                              children: [
                                  ...l.children,
                                  {
                                      id: action.payload.terminal.id,
                                      locationId: action.payload.terminal.locationId,
                                      isActive: action.payload.terminal.isActive,
                                      name: action.payload.terminal.name,
                                      type: action.payload.terminal.type,
                                      prefix: action.payload.terminal.prefix,
                                      url: action.payload.terminal.url,
                                      metadata: action.payload.terminal.metadata,
                                  },
                              ],
                          }
                ),
            }
        }

        case LOCATION_FORM_UNMOUNTED:
        case LOCATION_DETAIL_AGENDA_MOUNTED: {
            return { ...state, locationDetail: null }
        }

        case AGGREGATOR_SERVICE_DETAIL_FORM_AGENDA_MOUNTED: {
            return { ...state, loadingServiceDetail: true, serviceDetail: null }
        }

        case AGGREGATOR_CREATE_FORM_SUBMITTED:
        case AGGREGATOR_INTEGRATE_FORM_SUBMITTED:
        case AGGREGATOR_INTEGRATION_REQUESTED:
        case AGGREGATOR_UBER_STORES_REQUESTED:
        case AGGREGATOR_FOODMEOW_STORES_REQUESTED: {
            return { ...state, aggregatorForm: { ...state.aggregatorForm, loading: true, error: null } }
        }

        case AGGREGATOR_UBER_STORES_SUCCESS: {
            return {
                ...state,
                aggregatorForm: { ...state.aggregatorForm, loading: false, error: null, uber: action.payload },
            }
        }

        case AGGREGATOR_FOODMEOW_STORES_SUCCESS: {
            return {
                ...state,
                aggregatorForm: { ...state.aggregatorForm, loading: false, error: null, foodmeow: action.payload },
            }
        }

        case AGGREGATOR_UBER_STORES_ERROR:
        case AGGREGATOR_FOODMEOW_STORES_ERROR: {
            return { ...state, aggregatorForm: { ...state.aggregatorForm, loading: false, error: action.payload } }
        }

        case INTEGRATE_AGGREGATOR_SUCCESS: {
            return {
                ...state,
                data: (state.data || []).map(location =>
                    location.id !== action.payload.locationId
                        ? location
                        : {
                              ...location,
                              terminals: location.terminals.map(t =>
                                  t.id !== action.payload.terminalId ? t : { ...t, isActive: true }
                              ),
                          }
                ),
                locationsList: (state.locationsList || []).map(l =>
                    l.id !== action.payload.locationId
                        ? l
                        : {
                              ...l,
                              children: l.children.map(s =>
                                  s.id !== action.payload.terminalId ? s : { ...s, isActive: true }
                              ),
                          }
                ),
                aggregatorForm: { ...state.aggregatorForm, loading: false, error: null },
            }
        }

        default: {
            return state
        }
    }
}
